/**
 * Home Demo popup: a lead-capture form whose fields are fully configurable
 * as blocks on the "Home Demo Form" section (no hardcoded field list here).
 *
 * Submits natively and reloads the page, deliberately: Shopify's spam
 * protection only issues its captcha token on a native submit. So this script
 * validates before, and reopens the popup after.
 */

// Frees the button if a visitor abandons a visible hCaptcha task.
const SUBMIT_LOCK_TIMEOUT_MS = 30000;

class HomeDemoModal extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.formStep = this.querySelector('[data-home-demo-step="form"]');
    this.thanksStep = this.querySelector('[data-home-demo-step="thanks"]');
    this.bodyInput = this.querySelector('[data-home-demo-body]');
    this.fields = Array.from(this.querySelectorAll('[data-home-demo-field]'));

    this.querySelectorAll('[data-home-demo-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });

    // A back/forward cache restore replays the page exactly as it was, popup
    // and all, without re-running any of this — so start it closed.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.close();
    });

    // Blocks the submit only when validation fails — letting it through is
    // what triggers Shopify's captcha. stopImmediatePropagation keeps
    // Shopify's own submit listener from proceeding with an invalid form.
    if (this.form) {
      this.form.addEventListener('submit', (event) => {
        if (!this.validate()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        if (this.bodyInput) this.bodyInput.value = this.composeBody();
        this.lockSubmitButton();
      });
    }

    // Gates the click as well: cancelling it means no submit event fires at
    // all, so nothing downstream can act on an invalid form.
    this.querySelector('[data-home-demo-submit]')?.addEventListener('click', (event) => {
      if (!this.validate()) event.preventDefault();
    });

    // Any button anywhere on the page can open this modal with
    // data-home-demo-open — there's only meant to be one Home Demo Form
    // section per page, so no id-matching needed. Always opens on the form
    // step, since a submission leaves the popup showing the thank-you.
    document.querySelectorAll('[data-home-demo-open]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        this.showForm();
        this.open();
      });
    });

    this.initProductPickers();
    this.restoreAfterSubmit();
  }

  /* Reopens the popup after Shopify's post-submit reload, using the markers
     the section renders. No-op on a normal page load. */
  restoreAfterSubmit() {
    if (this.querySelector('[data-home-demo-posted]')) {
      this.clearSubmissionParams();

      // The marker lives in the URL, so refreshing or stepping back onto that
      // URL would re-show the thank-you. Only a forward navigation counts.
      if (this.isFreshNavigation()) {
        this.showThanks();
        this.open();
      }
      return;
    }

    // Rejected by Shopify — reopen on the form step, where the errors show.
    if (this.querySelector('[data-home-demo-errors]')) {
      this.open();
    }
  }

  /* 'navigate' is a genuine forward load, which the post-submit redirect is.
     Excludes both 'reload' and 'back_forward'. */
  isFreshNavigation() {
    return performance.getEntriesByType?.('navigation')?.[0]?.type === 'navigate';
  }

  /* Strips Shopify's post-submit params so a refresh lands on a clean URL. */
  clearSubmissionParams() {
    if (!window.history?.replaceState) return;

    const url = new URL(window.location.href);
    const keys = ['form_type', 'utf8', 'contact[posted_successfully]'];
    // Any one of them may be absent — bail only if none are present.
    if (!keys.some((key) => url.searchParams.has(key))) return;

    keys.forEach((key) => url.searchParams.delete(key));
    window.history.replaceState({}, '', url.pathname + url.search);
  }

  /* Shopify's captcha delays the navigation a second or two, so show progress
     and refuse a second click meanwhile. */
  lockSubmitButton() {
    const submitBtn = this.querySelector('[data-home-demo-submit]');
    if (!submitBtn) return;

    const originalLabel = submitBtn.textContent;
    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending...';

    setTimeout(() => {
      submitBtn.disabled = false;
      submitBtn.textContent = originalLabel;
    }, SUBMIT_LOCK_TIMEOUT_MS);
  }

  /* Product picker fields are a custom dropdown (not a native <select>) so
     each option can show a product thumbnail + name, which <option>
     elements can't do. A hidden input carries the actual submitted value
     and plugs into the same generic validate()/composeBody() logic as
     every other field. */
  initProductPickers() {
    this.querySelectorAll('[data-home-demo-product-picker]').forEach((picker) => {
      const hiddenInput = picker.querySelector('[data-home-demo-field]');
      const trigger = picker.querySelector('[data-home-demo-product-trigger]');
      const triggerText = picker.querySelector('[data-home-demo-product-trigger-text]');
      const list = picker.querySelector('[data-home-demo-product-list]');
      const options = Array.from(picker.querySelectorAll('[data-home-demo-product-option]'));

      const closeList = () => {
        list.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
      };

      const selectOption = (option) => {
        hiddenInput.value = option.dataset.value;
        triggerText.textContent = option.dataset.value;

        const thumbSrc = option.querySelector('img')?.src;
        let triggerThumb = trigger.querySelector('.home-demo-modal__product-thumb');
        if (thumbSrc) {
          if (!triggerThumb) {
            triggerThumb = document.createElement('img');
            triggerThumb.className = 'home-demo-modal__product-thumb';
            trigger.insertBefore(triggerThumb, triggerText);
          }
          triggerThumb.src = thumbSrc;
        }

        this.setFieldError(hiddenInput, '');
        closeList();
      };

      trigger.addEventListener('click', () => {
        const isOpen = !list.hidden;
        this.querySelectorAll('[data-home-demo-product-list]').forEach((otherList) => {
          otherList.hidden = true;
        });
        this.querySelectorAll('[data-home-demo-product-trigger]').forEach((otherTrigger) => {
          otherTrigger.setAttribute('aria-expanded', 'false');
        });
        if (!isOpen) {
          list.hidden = false;
          trigger.setAttribute('aria-expanded', 'true');
        }
      });

      options.forEach((option) => {
        option.addEventListener('click', () => selectOption(option));
        option.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            selectOption(option);
          }
        });
      });

      document.addEventListener('click', (event) => {
        if (!picker.contains(event.target)) closeList();
      });
    });
  }

  open() {
    this.hidden = false;
    document.body.style.overflow = 'hidden';
    this.fields[0]?.focus();
  }

  close() {
    this.hidden = true;
    document.body.style.overflow = '';
  }

  /* Shows/clears a field's error message and red-outline state. */
  setFieldError(input, message) {
    const field = input.closest('.home-demo-modal__field');
    const errorEl = field?.querySelector('.home-demo-modal__error');
    field?.classList.toggle('has-error', Boolean(message));
    if (errorEl) {
      errorEl.textContent = message || '';
      errorEl.hidden = !message;
    }
  }

  validate() {
    let valid = true;

    this.fields.forEach((input) => {
      const label = input.dataset.homeDemoLabel || 'This field';
      const type = input.dataset.homeDemoType;
      const value = input.value.trim();

      if (input.hasAttribute('data-home-demo-required') && !value) {
        this.setFieldError(input, `${label} is required`);
        valid = false;
        return;
      }

      if (!value) {
        this.setFieldError(input, '');
        return;
      }

      if (type === 'tel' && value.replace(/\D/g, '').length !== 10) {
        this.setFieldError(input, 'Enter a valid 10-digit mobile number');
        valid = false;
        return;
      }

      if (type === 'email' && !input.validity.valid) {
        this.setFieldError(input, 'Enter a valid email address');
        valid = false;
        return;
      }

      this.setFieldError(input, '');
    });

    return valid;
  }

  /* Composes a plain-text summary of every field so the notification email
     always shows everything collected, regardless of which fields a
     merchant has configured (Shopify's contact form only formats a few
     fixed keys nicely; this guarantees nothing gets lost). */
  composeBody() {
    return this.fields
      .map((input) => `${input.dataset.homeDemoLabel}: ${input.value.trim()}`)
      .join('\n');
  }

  showThanks() {
    this.formStep.hidden = true;
    this.thanksStep.hidden = false;
    // A long form leaves the dialog scrolled down.
    this.querySelector('.home-demo-modal__scroll')?.scrollTo({ top: 0 });
  }

  showForm() {
    this.formStep.hidden = false;
    this.thanksStep.hidden = true;
  }
}

if (!customElements.get('home-demo-modal')) {
  customElements.define('home-demo-modal', HomeDemoModal);
}
